import AccommodationAmenitiesAndPoliciesContent from 'components/AccommodationKit/AccommodationAmenitiesAndPoliciesContent'
import AccommodationPackageInclusionsList from 'components/AccommodationKit/AccommodationPackageInclusionsList'
import AccommodationRoomDetailsList from 'components/AccommodationKit/AccommodationRoomDetailsList'
import FormatCurrency from 'components/Common/FormatCurrency'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import Group from 'components/utils/Group'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import ImageGallery from 'LuxKit/Carousel/ImageGallery'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import type { ModalFooterActionProps } from 'LuxKit/Modal/ModalFooter'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import moment from 'moment'
import React, { useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

const IMAGE_PARAMS: ImageParams = {
  mobileAspectRatio: '16:8',
  tabletAspectRatio: '16:8',
  quality: 'good',
}

interface Props {
  upgradeType: App.LoyaltyStayUpgradeType
  originPackage: App.HotelPackage
  targetPackage: App.HotelPackage
  offer?: App.Offer | App.HotelOffer | App.VillaOffer | App.BundleOffer
  checkInDate?: string
  checkOutDate?: string
  hasApplyAction?: boolean
  hasRemoveAction?: boolean
  hasBackAction?: boolean
}

export interface LoyaltyStayUpgradeDetailsModalResult {
  upgradeType: App.LoyaltyStayUpgradeType | undefined
}

function LoyaltyStayUpgradeDetailsModal({
  upgradeType,
  originPackage,
  targetPackage,
  offer,
  checkInDate,
  checkOutDate,
  hasApplyAction,
  hasRemoveAction,
  hasBackAction,
}: Props) {
  const intl = useIntl()
  const modalElement = useModalElementContext<LoyaltyStayUpgradeDetailsModalResult | undefined>()
  const checkIn = useMemo(() => checkInDate ? moment(checkInDate) : undefined, [checkInDate])
  const checkOut = useMemo(() => checkOutDate ? moment(checkOutDate) : undefined, [checkOutDate])
  const { inclusions, bonusInclusions, luxPlusInclusions } = useOfferInclusions(targetPackage, { checkIn, checkOut })

  const dismiss = useCallback(() => { modalElement.resolve(undefined) }, [modalElement])

  const handleApplyUpgrade = useCallback(() => {
    modalElement.resolve({ upgradeType })
  }, [modalElement, upgradeType])

  const handleRemoveUpgrade = useCallback(() => {
    modalElement.resolve({ upgradeType: undefined })
  }, [modalElement])

  const applyRoomUpgradeLabel = intl.formatMessage({
    id: 'ad4VXhSMct7S2QBk-Bp6',
    defaultMessage: 'Apply free room upgrade',
    description: 'Primary action button to apply a free room upgrade in the loyalty stay upgrade modal',
  })
  const applyPackageUpgradeLabel = intl.formatMessage({
    id: 'hE21PyLHHDjtYF66LoJP',
    defaultMessage: 'Apply free package upgrade',
    description: 'Primary action button to apply a free package upgrade in the loyalty stay upgrade modal',
  })
  const removeLabel = intl.formatMessage({
    id: 'ZYU7-TdfKIAtpARovl2g',
    defaultMessage: 'Remove',
    description: 'Secondary action button to remove the applied upgrade in the loyalty stay upgrade modal',
  })
  const modalTitleRoom = intl.formatMessage({
    id: 'n2apUpHis9MTcwXXpixW',
    defaultMessage: 'Free room upgrade',
    description: 'Modal title for the loyalty room upgrade details in a modal dialog',
  })
  const modalTitlePackage = intl.formatMessage({
    id: 'r_45yZVgQ8MCS1Aj7TpL',
    defaultMessage: 'Free package upgrade',
    description: 'Modal title for the loyalty package upgrade details',
  })
  const upgradeToRoomLabel = intl.formatMessage(
    {
      id: 'svGorA-WbIH-ylkLL9iI',
      defaultMessage: 'Upgrade to {name}',
      description: 'Heading indicating upgrade destination for room type in loyalty stay upgrade modal',
    },
    { name: targetPackage.roomType.name },
  )
  const upgradeToPackageLabel = intl.formatMessage(
    {
      id: '3z7Y7W1Z49PexBF-mFTu',
      defaultMessage: 'Upgrade to {name}',
      description: 'Heading indicating upgrade destination for package in loyalty stay upgrade modal',
    },
    { name: targetPackage.name },
  )

  const primaryActionProps = useMemo((): ModalFooterActionProps | undefined => {
    if (!hasApplyAction) return
    return {
      children: upgradeType === 'room' ? applyRoomUpgradeLabel : applyPackageUpgradeLabel,
      onClick: handleApplyUpgrade,
    }
  }, [upgradeType, applyRoomUpgradeLabel, applyPackageUpgradeLabel, handleApplyUpgrade, hasApplyAction])

  const secondaryActionProps = useMemo((): ModalFooterActionProps | undefined => {
    if (!hasRemoveAction) return
    return {
      children: removeLabel,
      onClick: handleRemoveUpgrade,
    }
  }, [hasRemoveAction, handleRemoveUpgrade, removeLabel])

  return <ModalBase size="L">
    <ModalHeader
      title={<>
        {upgradeType === 'room' && modalTitleRoom}
        {upgradeType === 'package' && modalTitlePackage}
      </>}
      dismissible={!hasBackAction}
      onBackButtonClick={hasBackAction ? dismiss : undefined}
    />
    <ModalBody>
      {!!targetPackage.roomType.images.length && <ImageGallery
        images={targetPackage.roomType.images}
        imageParams={IMAGE_PARAMS}
      />}
      <ModalContent>
        <Group direction="vertical" gap={16}>
          <div>
            {upgradeType === 'room' && <>
              <Heading variant="heading4" as="s" colour="neutral-four">{originPackage.roomType.name}</Heading>
              <Heading variant="heading4" colour="neutral-two">{upgradeToRoomLabel}</Heading>
            </>}
            {upgradeType === 'package' && <>
              <Heading variant="heading4" as="s" colour="neutral-four">{originPackage.name}</Heading>
              <Heading variant="heading4" colour="neutral-two">{upgradeToPackageLabel}</Heading>
            </>}
            <BodyText variant="medium" colour="highlight-secondary">
              <FormattedMessage
                id="YjP7gq1gTD-cPGPwHum3"
                defaultMessage="Valued up to <b>{value}</b>"
                description="Upgrade value display in loyalty stay upgrade details modal"
                values={{
                  b: (chunks) => <b>{chunks}</b>,
                  value: <FormatCurrency value={targetPackage.value} />,
                }}
              />
            </BodyText>
          </div>
          <Group direction="vertical" gap={20}>
            {upgradeType === 'room' && <>
              <BodyText variant="large">{targetPackage.roomType.description}</BodyText>
              <AccommodationRoomDetailsList roomPackage={targetPackage} />
              <AccommodationAmenitiesAndPoliciesContent roomPackage={targetPackage} />
            </>}
            {upgradeType === 'package' && <Group direction="vertical" gap={8}>
              {!!luxPlusInclusions.length && <LuxPlusInclusions
                luxPlusInclusions={luxPlusInclusions}
                accommodationOffer={offer}
                hideUpsellModal
              />}
              {!!bonusInclusions.length && <AccommodationPackageInclusionsList title="bonus">
                {bonusInclusions.map((inclusion) => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} size="medium" />)}
              </AccommodationPackageInclusionsList>}
              <AccommodationPackageInclusionsList title={bonusInclusions.length ? 'additional' : undefined}>
                {inclusions.map((inclusion) => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} size="medium" />)}
              </AccommodationPackageInclusionsList>
            </Group>}
          </Group>
        </Group>
      </ModalContent>
    </ModalBody>
    <ModalFooter
      primaryActionProps={primaryActionProps}
      secondaryActionProps={secondaryActionProps}
    />
  </ModalBase>
}

export default LoyaltyStayUpgradeDetailsModal
